import React from 'react';
import { Icon, TableColumn, Text, Tooltip, TableRow } from '@upsales/components';
import { FolderType, FolderWithCount } from '../../AccountFiles';
import { useTranslation } from 'Components/Helpers/translate';
import InlineConfirm from 'Components/Dialogs/InlineConfirm';
import BemClass from '@upsales/components/Utils/bemClass';
import { confirmationText } from '../../Helpers/helpers';
import FolderModal from '../../FolderModal';
import './FolderRow.scss';

type Props = {
	folder: FolderType;
	openFolder: (folderId: number, name: string) => void;
	deleteFolder: (id: number, totalFiles: number) => void;
	changeNameOfFolder: (id: number, name: string) => void;
	folderMap: Map<number, FolderWithCount>;
};

const FolderRow = ({ folder, openFolder, deleteFolder, changeNameOfFolder, folderMap }: Props) => {
	const classes = new BemClass('FolderRow');
	const { t } = useTranslation();
	const folderData = folderMap.get(folder.id);
	const totalFiles = folderData?.childFiles.length ?? 0;
	const totalFolders = folderData?.totalSubFolderCount ?? 0;

	const lang = {
		removeFolder: t('file.removeFolder'),
		and: t('default.and').toLowerCase(),
		files: t('file.files').toLowerCase(),
		thisActionIsIrreversable: t('default.thisActionIsIrreversable'),
		delete: t('default.delete'),
		rename: t('file.rename'),
		with: t('default.with').toLowerCase(),
		folders: t('file.folders').toLowerCase(),
		folder: t('file.folder').toLowerCase(),
		file: t('file.file').toLowerCase()
	};

	return (
		<TableRow
			className={classes.b()}
			key={folder.id}
			onClick={(e: React.MouseEvent<HTMLElement>) => {
				e.stopPropagation();
				openFolder(folder.id, folder.name);
			}}
		>
			<TableColumn>
				<Text>
					<Icon className={classes.elem('folder-icon').b()} name={'folder'} />
					{folder.name}
				</Text>
			</TableColumn>
			<TableColumn>
				<Text>{totalFolders}</Text>
			</TableColumn>
			<TableColumn>
				<Text>{totalFiles}</Text>
			</TableColumn>
			<TableColumn className={classes.elem('last-column').b()} align="right">
				<Tooltip className={classes.elem('action-button').b()} title={lang.rename} position="left">
					<div onClick={e => e.stopPropagation()}>
						<FolderModal
							startName={folder.name}
							buttonText={lang.rename}
							onClick={name => {
								changeNameOfFolder(folder.id, name);
							}}
							clickableComponent={onClick => <Icon onClick={onClick} color="grey-6" name="edit" />}
						/>
					</div>
				</Tooltip>
				<Tooltip className={classes.elem('action-button').b()} title={lang.delete} position="left">
					{totalFiles > 0 ? (
						<InlineConfirm
							className={classes.elem('inline-confirm').b()}
							show
							onConfirm={() => deleteFolder(folder.id, totalFiles)}
							titleText={confirmationText(totalFolders, totalFiles, folder.name, lang)}
							bodyText={lang.thisActionIsIrreversable}
						>
							<Icon color="grey-6" name="trash-o" />
						</InlineConfirm>
					) : (
						<Icon
							color="grey-6"
							name="trash-o"
							onClick={e => {
								e.stopPropagation();
								deleteFolder(folder.id, totalFiles);
							}}
						/>
					)}
				</Tooltip>
			</TableColumn>
		</TableRow>
	);
};

export default FolderRow;
